import { Component, ChangeDetectionStrategy } from '@angular/core';
import { User } from '../../../model/user/user.model';
import { MatDialog } from '@angular/material/dialog';
import { LoginService } from '../../../authentication/login/login.service';
import { EulaComponent } from '../eula/eula.component';
import { ThemeService, TypeTheme } from '../../../theme/theme.service';
import { DestroyService } from '../../../shared/destroy.service';
import { catchError, finalize, takeUntil, timeout } from 'rxjs/operators';
import { DateLangFormatDialogComponent } from '../date-lang-format-dialog/date-lang-format-dialog.component';
import { FormMode } from '../../form/form-mode.enum';
import { HighlightApiKeyService } from '../highlight-api-key.service';
import { UserPreferenceService } from '../../../utils/user-preference.service';
import { of } from 'rxjs';

@Component({
  selector: 'app-profile-button',
  templateUrl: './profile-button.component.html',
  styleUrl: './profile-button.component.scss',
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProfileButtonComponent {
  protected readonly TypeTheme = TypeTheme;

  darkColors = ['#00496E', '#202475', '#005937', '#7F4100', '#670000', '#3A008B'];
  lightColors = ['#D6EEF6', '#DDEBFE', '#C7F8E6', '#FEF2D3', '#FDDDDF', '#E0D2F8'];
  bgColor: string;
  txtColor: string;
  initials: string;
  tooltip: string;
  borderColor: string;
  isImpersonation = false;

  constructor(
    private dialog: MatDialog,
    private destroy$: DestroyService,
    public loginService: LoginService,
    public themeService: ThemeService,
    public highlightApiKeyService: HighlightApiKeyService,
    public cacheService: UserPreferenceService
  ) {
    this.loginService.isImpersonate$.pipe(takeUntil(this.destroy$)).subscribe(isImpersonate => {
      this.isImpersonation = isImpersonate;
      this.generateAvatar();
    });
  }

  generateAvatar() {
    const user = this.loginService.currentUser;

    this.tooltip = this.getDisplayName(user);
    this.initials = this.getInitials(user);
    const initialsValue = this.initials
      .split('')
      .map(letter => letter.charCodeAt(0))
      .reduce((previous, current) => previous + current);
    const colorIndex = initialsValue % this.lightColors.length;
    this.bgColor = this.lightColors[colorIndex];
    this.txtColor = this.darkColors[colorIndex];
  }

  getInitials(user: User): string {
    if (user?.firstName && user?.lastName) {
      return (user.firstName[0] + user.lastName[0]).toUpperCase();
    }
    return user?.login[0].toUpperCase();
  }

  getDisplayName(user: User): string {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.login;
  }

  openDialogEULA(): void {
    this.dialog.open(EulaComponent, {
      panelClass: 'dialog-no-padding',
      autoFocus: 'dialog',
    });
  }

  openDialogDateLangFormat(): void {
    this.dialog.open(DateLangFormatDialogComponent, {
      data: {
        data: {},
        mode: FormMode.EDIT,
      },
      autoFocus: 'dialog',
    });
  }

  onSelectTheme(theme: TypeTheme): void {
    this.themeService.setTheme(theme);
  }

  logoutButton(): void {
    // Preferences pushed first, but a slow or failing API must not hold the user signed in.
    this.cacheService
      .push()
      .pipe(
        timeout(2000),
        catchError(() => of(null)),
        finalize(() => this.loginService.logout())
      )
      .subscribe();
  }
}
